import {useEffect, useRef} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {view} from '@sail/ui';
import {
  Conversation,
  LiveAnswersClient,
  LiveAnswersMessage,
  LiveAnswersStreamingCompletionData,
} from '@stripe-internal/conversations-client';
import {LoadingTextAsync} from '../../Conversation/LoadingTextAsync';
import {useAnalytics} from '../../AnalyticsProvider';
import {
  InlineLinkClickHandler,
  InlineLinkProps,
} from '../../StripeAssistant/LiveAnswersV2/InlineLinkClickHandler';
import {MarkdownText} from '../../StripeAssistant/LiveAnswersV2/MarkdownText';
import {SourcesDropdown} from '../../StripeAssistant/LiveAnswersV2/SourcesDropdown';
import {AgentCardComponent} from '../../StripeAssistant/LiveAnswersV2/AgentCard/AgentCardComponent';
import {useLiveAnswersStreamer} from '../../helpers/useLiveAnswersStreamer';

const messages = defineMessages({
  streaming_failed: {
    id: 'conversations_ui.chat.live_answers.assistant.streaming_failed',
    description:
      'Response text shown to users when an error occurs returning a response',
    defaultMessage: 'Sorry, an error occurred while processing your question.',
  },
  was_halted: {
    id: 'conversations_ui.chat.live_answers.assistant.was_halted',
    description:
      'Response text shown to users when we stopped streaming due to blocked content',
    defaultMessage:
      "I wasn't able to answer this question. Feel free to ask another or try rephrasing it for better results.",
  },
  long_running: {
    id: 'conversations_ui.chat.live_answers.assistant.long_running',
    description:
      'Text shown to users when a response is taking longer than usual. Only shown when the question is routed to the payments performance subagent.',
    defaultMessage: 'Working on it—this can take up to a minute.',
  },
});

type Props = {
  conversation?: Conversation;
  liveAnswersMessage: LiveAnswersMessage;
  liveAnswersClient: LiveAnswersClient;
  onDoneStreaming: (_onDoneData: LiveAnswersStreamingCompletionData) => void;
  onError: (error?: string) => void;
};

export function useLatest<T>(value: T) {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  });
  return ref;
}
export function LiveAnswersRowAssistant({
  conversation,
  liveAnswersMessage,
  liveAnswersClient,
  onDoneStreaming,
  onError,
}: Props) {
  const onCompleteRef = useLatest(onDoneStreaming);
  const onErrorRef = useLatest(onError);

  const analytics = useAnalytics();
  const intl = useIntl();

  const {streamer, content, isCancelled, isDoneStreaming, error, message} =
    useLiveAnswersStreamer(liveAnswersClient, liveAnswersMessage);

  useEffect(() => {
    streamer.startStreaming();
    return () => streamer.stopStreaming();
  }, [streamer]);

  useEffect(() => {
    if (isDoneStreaming && !error) {
      const fullContent = message.data?.content || '';
      const wasAnswerable = !message.data?.not_answerable && !isCancelled;
      const toolCalls = message.data?.tool_calls;

      onCompleteRef.current?.({
        content: isCancelled ? '' : fullContent,
        toolCalls,
        answerType: wasAnswerable ? 'answerable' : 'unanswerable',
        sources: message.sources,
      });
    }
  }, [isDoneStreaming, error, isCancelled, message, onCompleteRef]);

  useEffect(() => {
    if (error) {
      onErrorRef.current?.(error);
    }
  }, [error, onErrorRef]);

  const trimmedContent = content.trim();
  const isLongRunning = message.data?.is_long_running ?? false;
  const isPreparingResponse = message.data?.is_preparing_response ?? false;
  const progressUpdate = message.data?.progress_update ?? null;
  const loading = !isDoneStreaming && trimmedContent === '';
  const showSources =
    isDoneStreaming && !isCancelled && !message.data?.not_answerable;
  const lastAgentCall = message.data?.tool_calls?.length
    ? message.data.tool_calls[message.data.tool_calls.length - 1]
    : null;

  // It's possible to be in the completed state but with no content to
  // show. This happens when an error happens, which is handled
  // outside of this component. In that case we don't want to render
  // anything, even an empty div, which could cause layout issues
  if (isDoneStreaming && trimmedContent === '') {
    return null;
  }

  return (
    <view.div css={{stack: 'y', gap: 'medium'}}>
      {(loading || isPreparingResponse) && isLongRunning ? (
        <view.div css={{color: 'subdued'}}>
          {intl.formatMessage(messages.long_running)}
        </view.div>
      ) : null}
      {!isPreparingResponse && trimmedContent !== '' ? (
        <InlineLinkClickHandler
          onClick={({href, innerText, parentInnerText}: InlineLinkProps) => {
            analytics.action(
              'universal_help_live_answers_frontend_inline_link_clicked',
              {
                live_answers_source_url: href,
                live_answers_source_title: innerText,
                live_answers_source_context: parentInnerText,
              },
            );
          }}
        >
          <MarkdownText body={trimmedContent} />
        </InlineLinkClickHandler>
      ) : null}
      {/*
        We should NOT be using the conversation directly. This is needed currently
        by the PlanCard so it can check if it's the latest message or not. Need to
        rethink this in the API.
      */}
      {conversation && isDoneStreaming && lastAgentCall ? (
        <view.div css={{marginTop: '-4px'}}>
          <AgentCardComponent
            conversation={conversation}
            agentCall={lastAgentCall}
            messageId={message.messageId}
          />
        </view.div>
      ) : null}
      {isCancelled ? (
        <div>{intl.formatMessage(messages.was_halted)}</div>
      ) : null}
      {isDoneStreaming &&
      showSources &&
      message.sources &&
      message.sources.length > 0 ? (
        <SourcesDropdown sources={message.sources} />
      ) : null}
      {loading || isPreparingResponse ? (
        <LoadingTextAsync
          stage="just_one_moment"
          customText={progressUpdate ? `${progressUpdate}...` : ''}
          enableCannedMessageCycling={isLongRunning}
        />
      ) : null}
    </view.div>
  );
}
